import React from "react";

/*
 * Fleetroot tech stack illustration (white background, on-brand).
 * Orders flow in from your systems, pass through the three Fleetroot layers
 * (Delivery stack -> Route planner -> Telematics) and come out as
 * tracking, proof and reports.
 */

const font = "Inter, ui-sans-serif, system-ui, sans-serif";
const display = "'Space Grotesk', Inter, sans-serif";

const layers = [
  {
    y: 108,
    title: "Delivery stack",
    sub: "Dispatch · Order pooling · Rider app · POD · Customer portal",
    chips: ["Dispatch", "Pooling", "POD", "Portal"],
    accent: "#0E0E10",
  },
  {
    y: 186,
    title: "Route planner",
    sub: "Multi-stop optimization · Time windows · Zones · Live re-routing",
    chips: ["Optimize", "Windows", "Zones"],
    accent: "#4C30FF",
  },
  {
    y: 264,
    title: "Telematics",
    sub: "GPS devices · IoT sensors · Temperature · Fuel · Driver behaviour",
    chips: ["GPS device", "Sensors", "Temp", "Fuel"],
    accent: "#0E0E10",
  },
];

const Chip = ({ x, y, label, dark }) => {
  const w = label.length * 6.2 + 16;
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={w} height="18" rx="9" fill={dark ? "#0E0E10" : "#F4F4F0"} stroke={dark ? "none" : "#E6E6E0"} />
      <text x={w / 2} y="12.5" textAnchor="middle" fontFamily={font} fontSize="8.5" fontWeight="600" fill={dark ? "#FFFFFF" : "#3A3A40"}>
        {label}
      </text>
    </g>
  );
};

const Pill = ({ x, y, label, w = 92 }) => (
  <g transform={`translate(${x} ${y})`} filter="url(#st-shadow)">
    <rect width={w} height="26" rx="13" fill="#FFFFFF" stroke="#E6E6E0" />
    <text x={w / 2} y="17" textAnchor="middle" fontFamily={font} fontSize="9.5" fontWeight="600" fill="#0E0E10">
      {label}
    </text>
  </g>
);

const StackIllustration = ({ className = "" }) => (
  <svg
    viewBox="0 0 480 400"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="Fleetroot platform stack: delivery stack, route planner and telematics"
    data-testid="stack-illustration"
  >
    <defs>
      <pattern id="st-grid" width="24" height="24" patternUnits="userSpaceOnUse">
        <path d="M24 0H0V24" fill="none" stroke="#EEEEE9" strokeWidth="1" />
      </pattern>
      <filter id="st-shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="#0E0E10" floodOpacity="0.10" />
      </filter>
      <filter id="st-shadow-lg" x="-20%" y="-20%" width="140%" height="160%">
        <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#0E0E10" floodOpacity="0.12" />
      </filter>
    </defs>

    <rect width="480" height="400" fill="#FFFFFF" />
    <rect width="480" height="400" fill="url(#st-grid)" />

    {/* inputs */}
    <text x="240" y="22" textAnchor="middle" fontFamily={font} fontSize="8.5" fontWeight="600" fill="#6B6B73" letterSpacing="1.2">
      ORDERS IN
    </text>
    <Pill x={72} y={32} label="ERP / WMS" />
    <Pill x={194} y={32} label="E-commerce" />
    <Pill x={316} y={32} label="API / Sheets" />

    {/* connectors from inputs into the stack */}
    <g fill="none" stroke="#D6D6CE" strokeWidth="1.5">
      <path d="M118 58 C 118 80, 240 76, 240 100" />
      <path d="M240 58 V100" />
      <path d="M362 58 C 362 80, 240 76, 240 100" />
    </g>

    {/* spine with flowing data */}
    <line x1="240" y1="96" x2="240" y2="352" stroke="#EDEAFF" strokeWidth="6" strokeLinecap="round" />
    <line x1="240" y1="96" x2="240" y2="352" stroke="#4C30FF" strokeWidth="2" strokeLinecap="round" strokeDasharray="4 8" className="st-flow" />
    {[0, 1, 2].map((k) => (
      <circle key={k} r="4" fill="#4C30FF" cx="240" cy="96">
        <animate attributeName="cy" from="96" to="352" dur="3.6s" begin={`${k * 1.2}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="3.6s" begin={`${k * 1.2}s`} repeatCount="indefinite" />
      </circle>
    ))}

    {/* layers */}
    {layers.map((l, i) => {
      const w = 300 + i * 20;
      const x = 240 - w / 2;
      const dark = l.accent === "#4C30FF";
      let cx = x + 16;
      return (
        <g key={l.title} transform={`translate(0 ${l.y})`} filter="url(#st-shadow-lg)">
          <rect x={x} y="0" width={w} height="62" rx="14" fill={dark ? "#4C30FF" : "#FFFFFF"} stroke={dark ? "none" : "#E6E6E0"} />
          {!dark && <rect x={x} y="0" width="4" height="62" rx="2" fill="#0E0E10" />}
          <text x={x + 16} y="22" fontFamily={display} fontSize="13" fontWeight="700" fill={dark ? "#FFFFFF" : "#0E0E10"}>
            {l.title}
          </text>
          <text x={x + w - 62} y="21" fontFamily={font} fontSize="8" fontWeight="600" fill={dark ? "rgba(255,255,255,0.6)" : "#6B6B73"} letterSpacing="1.2">
            {`LAYER 0${i + 1}`}
          </text>
          {l.chips.map((c) => {
            const cw = c.length * 6.2 + 16;
            const el = <Chip key={c} x={cx} y={32} label={c} dark={false} />;
            cx += cw + 6;
            return dark ? (
              <g key={c} transform={`translate(${cx - cw - 6} 32)`}>
                <rect width={cw} height="18" rx="9" fill="rgba(255,255,255,0.16)" />
                <text x={cw / 2} y="12.5" textAnchor="middle" fontFamily={font} fontSize="8.5" fontWeight="600" fill="#FFFFFF">
                  {c}
                </text>
              </g>
            ) : (
              el
            );
          })}
        </g>
      );
    })}

    {/* outputs */}
    <g fill="none" stroke="#D6D6CE" strokeWidth="1.5">
      <path d="M240 340 C 240 362, 112 356, 112 372" />
      <path d="M240 340 V372" />
      <path d="M240 340 C 240 362, 368 356, 368 372" />
    </g>
    <g filter="url(#st-shadow)">
      <rect x="62" y="366" width="100" height="26" rx="13" fill="#0E0E10" />
      <text x="112" y="383" textAnchor="middle" fontFamily={font} fontSize="9.5" fontWeight="600" fill="#FFFFFF">
        Tracking links
      </text>
      <rect x="190" y="366" width="100" height="26" rx="13" fill="#0E0E10" />
      <text x="240" y="383" textAnchor="middle" fontFamily={font} fontSize="9.5" fontWeight="600" fill="#FFFFFF">
        Proof & billing
      </text>
      <rect x="318" y="366" width="100" height="26" rx="13" fill="#0E0E10" />
      <text x="368" y="383" textAnchor="middle" fontFamily={font} fontSize="9.5" fontWeight="600" fill="#FFFFFF">
        SLA reports
      </text>
    </g>
  </svg>
);

export default StackIllustration;
